import { ComponentType, ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '@/lib/utils';

/*
 * MobileTabBar — DESIGN_SYSTEM.md §2.3
 *
 * Persistent bottom nav with icon+label buttons. Designed for the
 * admin app on mobile (≤ md). Tab items navigate via wouter `<Link>`;
 * active state is computed from `useLocation()` matching `href`
 * exactly (or a prefix when `matchPrefix` is set on the item).
 *
 * Mount once per layout, after the page content. Pads the bottom of
 * the page implicitly via `pb-safe` on the page container — consumers
 * are responsible for that.
 */

export interface MobileTabItem {
  /** Link target. Omit when `onClick` is provided to render a button. */
  href?: string;
  icon: ComponentType<{ className?: string }>;
  label: ReactNode;
  /** Treat any path starting with `href` as active. */
  matchPrefix?: boolean;
  /** Optional trailing dot/count chip. */
  badge?: ReactNode;
  /** Anchor id (onboarding tour, analytics). */
  id?: string;
  /** Button-style tab (e.g. "More" → opens drawer). Mutually exclusive with `href`. */
  onClick?: () => void;
}

interface MobileTabBarProps {
  items: MobileTabItem[];
  className?: string;
}

export function MobileTabBar({ items, className }: MobileTabBarProps) {
  const [location] = useLocation();
  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-surface',
        'flex items-stretch lg:hidden',
        // env(safe-area-inset-bottom) for iOS notches.
        'pb-[env(safe-area-inset-bottom,0px)]',
        className,
      )}
      role="navigation"
      aria-label="Primary"
    >
      {items.map((item, idx) => {
        const isActive = item.href
          ? item.matchPrefix
            ? location.startsWith(item.href)
            : location === item.href
          : false;
        const Icon = item.icon;
        const sharedClass = cn(
          'group relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-colors',
          isActive ? 'text-brand-500' : 'text-soft active:text-ink',
        );
        const body = (
          <>
            <Icon className="h-5 w-5" />
            <span className="text-[10px] font-medium tracking-wide">{item.label}</span>
            {item.badge && (
              <span className="absolute top-1 right-[calc(50%-18px)]">
                {item.badge}
              </span>
            )}
          </>
        );

        if (item.onClick) {
          return (
            <button
              key={item.id ?? `tab-${idx}`}
              type="button"
              id={item.id}
              onClick={item.onClick}
              className={sharedClass}
            >
              {body}
            </button>
          );
        }

        return (
          <Link
            key={item.href ?? `tab-${idx}`}
            href={item.href ?? '#'}
            id={item.id}
            className={sharedClass}
            aria-current={isActive ? 'page' : undefined}
          >
            {body}
          </Link>
        );
      })}
    </nav>
  );
}
